'use client';

import { Button, cn } from '@shared/components/shadcn';
import { Delete, Error, Success } from '@shared/icons';
import { useTranslations } from 'next-intl';
import type { FileListItemProps } from '../types';

function FileListItem({ file, onRemove, disabled = false }: FileListItemProps) {
  const t = useTranslations('global_dialogs.kyc_verification.manual_upload');

  const handleRemove = () => {
    onRemove(file.id);
  };

  const hasError = Boolean(file.error);

  if (hasError) {
    return (
      <div
        className="flex w-full min-w-0 items-start gap-4 p-4 border border-error-foreground rounded-lg transition-colors"
        role="alert"
        data-track-container="Document"
        data-testid="file-error-alert"
      >
        <div className="flex items-center justify-center rounded-full flex-shrink-0" data-track-name="Error">
          <Error size="small" className="text-error-foreground w-7 h-7" />
        </div>
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <p className="link-label-explanatory truncate text-error-foreground" data-testid="file-name">
            {file.name}
          </p>
          <p className="paragraph-s text-error-foreground" data-testid="file-error">
            {file.error}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={handleRemove}
          disabled={disabled}
          data-testid="remove-file-button"
          data-track-name="Delete"
          aria-label={t('remove_file', { fileName: file.name })}
          className="text-error-foreground flex-shrink-0"
        >
          <Delete size="small" />
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn('flex w-full min-w-0 items-start gap-4 p-4 border rounded-lg transition-colors', 'border-border', disabled && 'opacity-50 cursor-not-allowed')}
      data-track-container="Document"
      data-testid="file-list-item"
    >
      <div className="flex items-center justify-center p-1 rounded-full bg-background-inverse flex-shrink-0" data-track-status="Success" data-testid="file-success-icon">
        <Success size="small" className="text-background" />
      </div>
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <p className="link-label-explanatory truncate text-foreground" data-testid="file-name">
          {file.name}
        </p>
        <p className="paragraph-s text-foreground-subtle" data-testid="file-size">
          {file.sizeFormatted}
        </p>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={handleRemove}
        disabled={disabled}
        data-testid="remove-file-button"
        data-track-name="Delete"
        aria-label={t('remove_file', { fileName: file.name })}
        className="text-foreground flex-shrink-0"
      >
        <Delete size="small" />
      </Button>
    </div>
  );
}

export { FileListItem };
